// Cashfree hand-off. All secrets stay on the backend:
//   POST /payment/order  → { orderId, paymentSessionId, mode, amount, currency, breakdown, orgName }
//   POST /payment/verify → { status: "ACTIVATED" | "PENDING" | "FAILED", plan, subscriptionEndDate, message }
// The browser only ever sees the payment session id and the order id.
import axios from "axios";

const SDK_JS = "https://sdk.cashfree.com/js/v3/cashfree.js";
const base = () => process.env.REACT_APP_BACKEND_URL;
const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const loadSdk = () =>
  new Promise((resolve, reject) => {
    if (window.Cashfree) return resolve();
    const existing = document.querySelector(`script[src="${SDK_JS}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Could not load payment window")));
      return;
    }
    const s = document.createElement("script");
    s.src = SDK_JS;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Could not load payment window"));
    document.body.appendChild(s);
  });

// Asks the backend for the real status of an order (backend checks with Cashfree).
export const verifyOrder = async ({ orderId, token }) => {
  try {
    const { data } = await axios.post(`${base()}/payment/verify`, { orderId }, auth(token));
    return data;
  } catch (e) {
    return e?.response?.data || { status: "FAILED", message: "Payment could not be verified." };
  }
};

// Creates the order on the backend and opens the Cashfree popup.
// When the popup closes, the backend checks the real payment attempt with Cashfree:
//   ACTIVATED / FAILED / PENDING → onComplete(orderId)  (result page shows success / reason / confirming)
//   CANCELLED (no attempt made) → onDismiss(message)    (stay on checkout)
// Errors before the popup opens (e.g. order creation) are thrown to the caller.
export const startPayment = async ({ orgId, plan, cycle, token, onComplete, onDismiss }) => {
  const { data: order } = await axios.post(`${base()}/payment/order`, { orgId, plan, cycle }, auth(token));

  await loadSdk();
  const cashfree = window.Cashfree({ mode: order.mode || "sandbox" });

  const result = await cashfree.checkout({
    paymentSessionId: order.paymentSessionId,
    redirectTarget: "_modal",
  });

  // Some UPI/bank flows leave the page; Cashfree's return_url brings the user to the result page.
  if (result?.redirect) return;

  const check = await verifyOrder({ orderId: order.orderId, token });
  if (check?.status === "CANCELLED") return onDismiss?.(check.message);
  onComplete?.(order.orderId);
};

// Re-reads the org so localStorage "subs" reflects the new plan without re-login.
export const refreshOrgPlan = async ({ username, orgId, token }) => {
  const { data } = await axios.get(
    `${base()}/organization/get-by-admin?username=${username}&orgId=${orgId}`,
    auth(token),
  );
  if (data?.subscription) localStorage.setItem("subs", JSON.stringify(data.subscription));
  return data;
};